import { useEffect } from "react";
import { motion } from "framer-motion";

export const Reveal = ({ children, delay = 0, y = 24, className = "", ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.75, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

/** A whole section fading up as it enters the viewport, for page-level rhythm rather than single blocks. */
export const SectionFade = ({ children, className = "", ...rest }) => (
  <motion.section
    initial={{ opacity: 0, y: 46 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.12 }}
    transition={{ duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
    className={className}
    {...rest}
  >
    {children}
  </motion.section>
);

const SITE = "KickStartKenyaFootball";
const SLOGAN = "Our Youth. Our Plan. Our Football.";
const STANDFIRST =
  "An independent partner bridging football, youth, community and the diaspora. Our Youth. Our Plan. Our Football. www.KickStartKenyaFootball.com";
const BRAND_RX = /kick[\s-]?start[\s-]?kenya[\s-]?football/i;

// Per-page share card, drawn on the backend from that page's own figures.
export const cardUrl = (kind, slug) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/card/${kind}${slug ? `/${slug}` : ""}.png`;

const setMeta = (attr, key, value) => {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!value) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

export const useSeo = ({ title, description, image, type = "website" }) => {
  useEffect(() => {
    const fullTitle = title ? (BRAND_RX.test(title) ? title : `${title} — ${SITE}`) : `${SITE} — ${SLOGAN}`;
    document.title = fullTitle;
    const url = window.location.href;
    // Every page shares the short-logo card and the house description unless it supplies its own.
    const card = image || `${window.location.origin}/social-card.png`;
    const blurb = description || STANDFIRST;

    setMeta("name", "description", blurb);
    setMeta("property", "og:site_name", SITE);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", blurb);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", card);
    setMeta("property", "og:image:alt", `${SITE} — ${STANDFIRST}`);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", blurb);
    setMeta("name", "twitter:image", card);
    setMeta("name", "twitter:image:alt", `${SITE} — ${STANDFIRST}`);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url.split("?")[0]);
  }, [title, description, image, type]);
};
